import React, { FC, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

interface Prop {
  /* Stagger, in ms, applied once the element enters the viewport. */
  delay?: number
  className?: string
}

const Wrapper = styled.div<{ shown: boolean; delay: number }>`
  opacity: ${(props) => (props.shown ? 1 : 0)};
  transform: ${(props) => (props.shown ? 'none' : 'translateY(26px)')};
  transition: opacity 0.7s cubic-bezier(0.22, 0.61, 0.36, 1)
      ${(props) => props.delay}ms,
    transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1)
      ${(props) => props.delay}ms;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`

/*
 * One shared, rAF-throttled listener drives every Reveal on the page rather
 * than an IntersectionObserver per instance. IO delivery is tied to the
 * rendering lifecycle, so a throttled or backgrounded tab can leave callbacks
 * undelivered — and content that needs an animation to become visible must
 * never be able to get stuck at opacity 0. A direct rect check cannot.
 */
type Check = () => void
const subscribers = new Set<Check>()
let lastRun = 0
let trailing: number | null = null

const runAll = () => {
  lastRun = Date.now()
  subscribers.forEach((fn) => fn())
}

/* Time-based throttle rather than requestAnimationFrame: rAF callbacks do not
   run while the document is hidden, which would leave revealed content stuck
   at opacity 0 in a backgrounded tab. Scroll events still fire there. */
const notify = () => {
  if (Date.now() - lastRun > 100) {
    runAll()
  } else if (trailing === null) {
    trailing = window.setTimeout(() => {
      trailing = null
      runAll()
    }, 100)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('scroll', notify, { passive: true })
  window.addEventListener('resize', notify)
  document.addEventListener('visibilitychange', notify)
}

const Reveal: FC<Prop> = ({ children, delay = 0, className }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) {
      setShown(true)
      return
    }

    let done = false
    const check = () => {
      if (done) return
      const rect = node.getBoundingClientRect()
      const viewportH = window.innerHeight || document.documentElement.clientHeight
      // Trigger slightly before the element is fully on screen.
      if (rect.top < viewportH * 0.94 && rect.bottom > 0) {
        done = true
        subscribers.delete(check)
        setShown(true)
      }
    }

    subscribers.add(check)
    check() // above-the-fold content shows without waiting for a scroll

    // Backstop: if nothing ever scrolls, reveal anyway rather than stay blank.
    const failsafe = window.setTimeout(() => {
      if (!done) {
        done = true
        subscribers.delete(check)
        setShown(true)
      }
    }, 1800)

    return () => {
      subscribers.delete(check)
      window.clearTimeout(failsafe)
    }
  }, [])

  return (
    <Wrapper ref={ref} shown={shown} delay={delay} className={className}>
      {children}
    </Wrapper>
  )
}

export default Reveal
